import { Component, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { Location } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { filter } from 'rxjs';
import { MotesMediaPlayerService } from '../media-player.service';
import { MediaEvent, MediaState, MediaEventType, Media, MediaType, MotesMediaPlayerPosition } from '../../models';
import { AutoSub, AutoSubOnDestroy } from '../../providers/auto-sub';
import { Animations } from '../../animations';

@AutoSubOnDestroy()
@Component({
    selector: 'video-player',
    templateUrl: 'video-player.component.html',
    styleUrls: ['./video-player.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [Animations.slideFromTop(300)]
})
export class VideoPlayerComponent {

    @ViewChild('video') video: ElementRef<HTMLVideoElement>;

    public media: Media = null;

    public playing: boolean = false;
    public fullscreenImage: Media = null;
    public fullscreenVideo: Media = null;

    private currentEvent: MediaEvent;

    public Infinity = Infinity;
    // Sanitize url function for generic pipe with 'this' context 
    public sanitizeUrl = (url) => this.sanitizeVideoUrl(url);

    // Expose to view
    public MediaType = MediaType;
    public MotesMediaPlayerPosition = MotesMediaPlayerPosition;

    constructor(
        private mediaPlayerService: MotesMediaPlayerService,
        private location: Location,
        private sanitizer: DomSanitizer,
    ) { }

    ngOnInit() {
        AutoSub(this).reg['mediaEv'] = this.mediaPlayerService.watchMediaEvents().pipe(
            filter((event) => event.type == MediaEventType.PRELOAD_MEDIA || event.type == MediaEventType.STATEUPDATE || event.type == MediaEventType.SHOW_IMAGE)).subscribe((event: MediaEvent) => {
            this.currentEvent = event;
            if(event.type == MediaEventType.PRELOAD_MEDIA) {
                console.log("Got preload media event ", event);
                // Enable media player component for the specific media type (start preloading media)
                this.media = event.media;
            }
            else if(event.type == MediaEventType.STATEUPDATE) {
                this.media = event.media;
                this.onMediaEvent(event);
            }
            else {
                if(event.media.type == MediaType.IMAGE) this.fullscreenImage = event.media;
                else this.fullscreenVideo = event.media;
            }
        });

        AutoSub(this).reg['hashChange'] = this.location.subscribe((event: PopStateEvent): void => {
            if(this.currentEvent && this.currentEvent.state == MediaState.PLAYING && this.currentEvent.media.type != MediaType.SOUND) {
                this.mediaPlayerService.stopPlayer();
            }
        });
    }

    public hideFullscreenMedia() {
        this.fullscreenImage = null;
        this.fullscreenVideo = null;
    }

    public async onMediaEvent(ev: MediaEvent) {

        if(ev.state == MediaState.PLAYING) {
            this.media = ev.media;
            this.playing = true;

            // Append hash for back naviation on video and image
            const currentMedia = this.currentEvent.media;
            if (currentMedia.type == MediaType.VIDEO ||
                currentMedia.type == MediaType.YOUTUBE_VIDEO ||
                currentMedia.type == MediaType.FILMCENTRALEN_VIDEO
            ) {
                this.location.go(this.location.path(true) + '#video-' + currentMedia.key);
            } else if (currentMedia.type == MediaType.IMAGE) {
                this.location.go(this.location.path(true) + '#image-' + currentMedia.key);
            }
        }
        else if(ev.state == MediaState.NONE) {
            // Reset state
            this.media = ev.media;
            this.playing = false;

            // Remove hash when user stops playing (if hash is already in the url)
            let baseUrl = this.location.path().split('#')[0]; // without hash
            if(baseUrl.includes('#')) this.location.go(baseUrl);
        }

    }

    public sanitizeVideoUrl(url) {
        if(!url) {
            console.warn("Trying to sanitize empty video url");
            return "";
        }
        return this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }

    public stopPlayer(goBack: boolean = false): void {
        // Navigate back (in order to keep clean url history for backbutton)
        if(goBack) this.location.back();
        else this.mediaPlayerService.stopPlayer();
    }

    public mediaEnded(goBack: boolean = false): void {
        // Navigate back (in order to keep clean url history for backbutton)
        if(goBack) this.location.back();
        else this.mediaPlayerService.mediaEnded();
    }

}
